// RITA-1498 (Nutzungserfassung Phase 1b) — axios-Interceptor (Konzept 4.1 Punkt 3).
// Der Legacy-Weg services/useRequest.ts schickt GraphQL per axios an /backend; PdfPreview
// nutzt axios für Dateien. Es gibt keine gemeinsame Instanz, daher wird der Interceptor an der
// Default-Instanz installiert. Name aus dem Abfragetext (nie Variablen), sonst Methode+Pfadmuster.
import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { graphqlOperation, restName } from './apiName';
import type { UsageEventInput } from './tracker';

interface PendingCall {
  startedAt: number;
  name: string;
  kind: 'query' | 'mutation' | 'rest';
}

const readQueryText = (data: unknown): string | null => {
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data);
      return readQueryText(parsed);
    } catch {
      return null;
    }
  }
  if (typeof data === 'object' && data !== null && typeof (data as { query?: unknown }).query === 'string') {
    return (data as { query: string }).query;
  }
  return null;
};

const describeRequest = (config: AxiosRequestConfig): Pick<PendingCall, 'name' | 'kind'> => {
  const queryText = readQueryText(config.data);
  if (queryText !== null) return graphqlOperation(queryText);
  return { name: restName(config.method, config.url), kind: 'rest' };
};

export const installAxiosUsageInterceptor = (
  instance: AxiosInstance,
  record: (input: UsageEventInput) => void,
  now: () => number = () => Date.now(),
): (() => void) => {
  const pending = new WeakMap<object, PendingCall>();

  const report = (config: AxiosRequestConfig | undefined, ok: boolean, code?: string): void => {
    if (!config) return;
    const call = pending.get(config);
    if (!call) return;
    pending.delete(config);
    const props: UsageEventInput['props'] = { kind: call.kind, ok };
    if (code) props.code = code;
    try {
      record({ type: 'api', name: call.name, value: Math.max(0, now() - call.startedAt), props });
    } catch {
      // Die Erfassung darf den Aufruf nie stören.
    }
  };

  const requestId = instance.interceptors.request.use((config) => {
    try {
      pending.set(config, { startedAt: now(), ...describeRequest(config) });
    } catch {
      // siehe report
    }
    return config;
  });

  const responseId = instance.interceptors.response.use(
    (response: AxiosResponse) => {
      report(response.config, true);
      return response;
    },
    (error: AxiosError) => {
      const status = error && error.response ? String(error.response.status) : 'network';
      report(error ? error.config : undefined, false, status);
      return Promise.reject(error);
    },
  );

  return () => {
    instance.interceptors.request.eject(requestId);
    instance.interceptors.response.eject(responseId);
  };
};

export default installAxiosUsageInterceptor;
